import React, { Fragment, useEffect, useState } from "react";
import PageHeader from "../../common/components/PageHeader";
import { Avatar, Box, Button, Card, CardContent, Chip, Dialog, DialogContent, Grid, IconButton, Stack, styled, TextField, Typography, useTheme } from "@mui/material";
import CommonButton from "../../common/components/CommonButton";
import { Add, Close, DeleteForever, FiberManualRecord, InfoOutlined, LinkRounded, Star, WatchLater } from "@mui/icons-material";
import CommonCard from "../../common/components/CommonCard";
import CommonDialog from "../../common/components/CommonDialog";
import AddParentAccount from "./AddParentAccount";
import { useDispatch, useSelector } from "react-redux";
import { deleteParent, getParents, resendInvitation } from "../../api/parentApi";
import LoadingSpinner from "../../common/components/LoadingSpinner";
import { setParentList } from "../../features/parentSlice";
import { setLoading } from "../../features/authSlice";
import { showNotification } from "../../common/components/Notification/ToastifyNotification";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { checkBookingSession, confirmBooking, getEventByInvite } from "../../api/sessionApi";
import { ExpierienceYears, ServiceOffer, UserTypeId } from "../../common/const";
import { mentorsByStudent } from "../../api/mentorApi";
import CommonBookSession from "../../common/components/CommonBookSession";


const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '12px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
        transform: 'translateY(-2px)',
    },
}));

const StyledChip = styled(Chip)({
    backgroundColor: '#F3F4F6',
    color: '#4B5563',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '6px',
    height: '28px',
});

const ViewProfileButton = styled(Button)({
    color: '#1E3A8A',
    borderColor: '#1E3A8A',
    fontWeight: 500,
    textTransform: 'none',
    borderRadius: '8px',
    height: '35px',
    '&:hover': {
        borderColor: '#1E3A8A',
        backgroundColor: '#F0F4FF',
    },
});

const StyledDialog = styled(Dialog)({
    '& .MuiDialog-paper': {
        borderRadius: '16px',
        maxWidth: '540px',
        width: '100%',
    },
});

const BookSessionButton = styled(Button)({
    backgroundColor: '#FBB424',
    color: '#1f2937',
    fontWeight: 500,
    textTransform: 'none',
    borderRadius: '8px',
    height: '35px',
    '&:hover': {
        backgroundColor: '#F59E0B',
    },
});


const ParentAccount = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user, loading } = useSelector((state) => state.auth);
    const { parentList } = useSelector((state) => state.parent);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [parent, setParent] = useState(null);
    const [viewProfileDialog, setViewProfileDialog] = useState(false);
    const [bookSessionDialog, setBookSessionDialog] = useState(false);
    const [confirmationDialog, setConfirmationDialog] = useState(false);
    const [selectedMentor, setSelectedMentor] = useState(null);
    const [sessionNote, setSessionNote] = useState('');
    const [assigned, setAssigned] = useState(null);
    const [mentorId, setMentorId] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [coachData, setCoachData] = useState(null);
    const [open, setOpen] = useState(false);
    const [initialData, setInitialData] = useState(null);
    const [eventData, setEventData] = useState(null);

    const getTutorData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await mentorsByStudent(user?.student_id);
            const data = response.data.find((a) => a.mentor_profile_id === mentorId);
            loadImageAsBlob(data?.mentor?.user?.avatar);
            setCoachData(data);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    const loadImageAsBlob = async (avatarPath) => {
        try {
            const fullUrl = `${process.env.REACT_APP_BASE_URL}/file?bname=${avatarPath}&download=false`;
            const response = await fetch(fullUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();
            const objectUrl = URL.createObjectURL(blob);
            setPreviewUrl(objectUrl);
        } catch (error) {
            console.error('Error loading image:', error);
        }
    };


    useEffect(() => {
        if (user?.student_id && assigned !== 'DISCOVERY') {
            getTutorData();
        }
    }, [assigned]);

    const getParentList = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getParents(user?.student_id);
            dispatch(setParentList({
                list: response.data
            }));
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id) {
            getParentList();
        }
    }, [user?.student_id]);

    const handleViewProfile = (mentor) => {
        setSelectedMentor(mentor);
        setViewProfileDialog(true);
    };

    const handleCloseAll = () => {
        setViewProfileDialog(false);
        setBookSessionDialog(false);
        setConfirmationDialog(false);
        setSelectedMentor(null);
        setSessionNote('');
        setEventData(null);
    };

    const getEventData = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await getEventByInvite(initialData?.payload?.invitee?.uri);
            setEventData(response.data);
            setBookSessionDialog(true);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    useEffect(() => {
        if (initialData !== null) {
            dispatch(setLoading({
                loading: true
            }));
            setTimeout(() => {
                getEventData();
            }, 5000);
        }
    }, [initialData]);


    const handleConfirmBooking = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const data = {
                "mentor_profile_id": coachData?.mentor_profile_id,
                "student_id": user?.student_id,
                "service_offers": [ServiceOffer.counsellor],
                "notes": sessionNote,
                "status": "CONFIRMED",
                "invitee_email": eventData.invitee_email,
                "calendly_event_uri": eventData.calendly_event_uri,
                "calendly_invitee_uri": eventData.calendly_invitee_uri
            }
            const response = await confirmBooking(data);
            if (response) {
                dispatch(setLoading({
                    loading: false
                }));
                setBookSessionDialog(false);
                setSessionNote(null);
                setEventData(null);
                setInitialData(null);
                handleCloseAll();
                showNotification('Session booked successfully', 'success');
            }
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    };

    const checkSessionBook = async () => {
        try {
            dispatch(setLoading({
                loading: true
            }));
            const response = await checkBookingSession(ServiceOffer.counsellor, user?.student_id);
            setMentorId(response?.data?.mentor_profile_id);
            setAssigned(response?.data.type);
            dispatch(setLoading({
                loading: false
            }));
        } catch (err) {
            const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
            showNotification(errorMessage, 'error');
            dispatch(setLoading({
                loading: false
            }));
        }
    }

    useEffect(() => {
        if (user?.student_id) {
            checkSessionBook();
        }
    }, [user]);

    return (
        <Fragment>
            {loading && (
                <LoadingSpinner />
            )}
            <AddParentAccount
                addOpen={addOpen}
                setAddOpen={setAddOpen}
                getParentList={getParentList}
            />
            <CommonDialog
                open={deleteOpen}
                size="xs"
                content={(
                    <center>
                        <Typography sx={{ fontWeight: 600, fontSize: '22px', mt: 0.5, mb: 1 }}>
                            Are you sure you want to remove access for {parent?.user?.first_name}?
                        </Typography>
                    </center>
                )}
                actionButtons={(
                    <Fragment>
                        <CommonButton
                            label="Yes"
                            size="small"
                            background={theme.palette.secondary.main}
                            color={theme.palette.primary.main}
                            handleClick={async () => {
                                try {
                                    dispatch(setLoading({
                                        loading: true
                                    }));
                                    const response = await deleteParent(parent.parent_id);
                                    if (response) {
                                        showNotification('Parent account deleted successfully', 'success');
                                        setDeleteOpen(false);
                                        setParent(null);
                                        getParentList();
                                        dispatch(setLoading({
                                            loading: false
                                        }));
                                    }
                                } catch (err) {
                                    const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                    showNotification(errorMessage, 'error');
                                    dispatch(setLoading({
                                        loading: false
                                    }));
                                }
                            }}
                        />
                        <CommonButton
                            label="No"
                            size="small"
                            background={theme.palette.background.main}
                            border={`1px solid ${theme.palette.background.light}`}
                            handleClick={() => {
                                setParent(null);
                                setDeleteOpen(false);
                            }}
                        />
                    </Fragment>
                )}
            />
            <PageHeader
                title="My Parent Account"
                content={
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                            Home
                        </Typography>
                        <FiberManualRecord sx={{ fontSize: 8, color: 'text.secondary' }} />
                        <Typography color="text.secondary" sx={{ fontSize: 14, }}>
                            My Parent Account
                        </Typography>
                    </Stack>
                }
                buttonContent={
                    user?.user_type_id === UserTypeId.Student &&
                    (<CommonButton
                        width="140px"
                        label="Add Parent"
                        fontWeight={600}
                        startIcon={<Add />}
                        marginBottom="0px"
                        handleClick={() => setAddOpen(true)}
                    />)}
            />
            <Box
                sx={{
                    background: `linear-gradient(135deg, ${theme.palette.cardbackground.primary} 0%, ${theme.palette.cardbackground.secondary} 100%)`,
                    padding: '15px 15px 15px 25px',
                    borderRadius: 1,
                    mt: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    border: `1px solid ${theme.palette.cardbackground.light}`
                }}
            >
                <img src="/Images/usergroup.png" alt="user group" />
                <Box>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%'
                        }}
                    >
                        <Typography sx={{ fontSize: 15, fontWeight: 800 }} variant='h4' color="cardbackground.dark">My Parent Accounts Linked</Typography>
                        <Box sx={{ background: theme.palette.success.main, pt: 0.2, pb: 0.3, pl: 1, pr: 1, borderRadius: 1 }}>
                            <Typography sx={{ fontSize: 12, color: 'white' }}>{parentList.length} Parents</Typography>
                        </Box>
                    </Box>
                    <Typography sx={{ fontSize: 12 }} color={theme.palette.background.dark}>Your parents can view your progress and receive updates about your college preparation journey.</Typography>
                </Box>
            </Box>
            <Grid container spacing={2}>
                <Grid item md={9} sm={12}>
                    <Grid container spacing={2}>
                        {parentList.map((a, i) => (
                            <Grid item xs={12} md={6} lg={6}>
                                <CommonCard
                                    borderRadius={2}
                                    border={`1px solid ${theme.palette.background.secondary}`}
                                    content={
                                        <Fragment>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: 12
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1.5,
                                                        width: '100%'
                                                    }}
                                                >
                                                    <img src="/Images/user.png" height="39px" alt="user" />
                                                    <Box>
                                                        <Typography sx={{ fontSize: 17, fontWeight: 'bold' }}>{a?.user?.first_name}</Typography>
                                                        <Typography color="background.dark" fontSize={13}>{a?.relationship_type}</Typography>
                                                    </Box>
                                                </Box>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 1
                                                    }}
                                                >
                                                    {a.accepted_date && (
                                                        <Box
                                                            sx={{
                                                                background: theme.palette.secondary.main,
                                                                color: theme.palette.primary.main,
                                                                padding: '0px 10px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                borderRadius: '8px',
                                                                gap: 0.5
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 10,
                                                                    height: 10,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: theme.palette.success.main
                                                                }}
                                                            />
                                                            <Typography fontSize={14}>Active</Typography>
                                                        </Box>
                                                    )}
                                                    {user?.user_type_id === UserTypeId.Student &&
                                                        (
                                                            <Fragment>
                                                                <CommonButton
                                                                    height="32px"
                                                                    label="Remove"
                                                                    marginBottom="0px"
                                                                    background="white"
                                                                    border={`1px solid ${theme.palette.background.light}`}
                                                                    color="red"
                                                                    width="100px"
                                                                    startIcon={<DeleteForever sx={{ color: 'red', fontSize: 20 }} />}
                                                                    handleClick={() => {
                                                                        setParent(a);
                                                                        setDeleteOpen(true);
                                                                    }}
                                                                />
                                                                {!a?.accepted_date && (
                                                                    <CommonButton
                                                                        height="32px"
                                                                        label="Resend Invitation"
                                                                        marginBottom="0px"
                                                                        color={theme.palette.primary.main}
                                                                        background={theme.palette.secondary.main}
                                                                        width="135px"
                                                                        handleClick={async () => {
                                                                            try {
                                                                                dispatch(setLoading({
                                                                                    loading: true
                                                                                }));
                                                                                const response = await resendInvitation(a.parent_id);
                                                                                if (response.success) {
                                                                                    showNotification(response.message, 'success');
                                                                                    dispatch(setLoading({
                                                                                        loading: false
                                                                                    }));
                                                                                }
                                                                            } catch (err) {
                                                                                const errorMessage = err.response?.data?.errors?.[0]?.message || err.response?.data?.message || err?.response?.data?.error;
                                                                                showNotification(errorMessage, 'error');
                                                                                dispatch(setLoading({
                                                                                    loading: false
                                                                                }))
                                                                            }
                                                                        }}
                                                                    />
                                                                )}
                                                            </Fragment>)}
                                                </Box>
                                            </Box>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    gap: 1
                                                }}
                                            >
                                                <CommonCard
                                                    content={
                                                        <Fragment>
                                                            <Typography sx={{ fontWeight: 600, fontSize: 13, wordBreak: 'break-word' }}>{a?.user?.email || '-'}</Typography>
                                                            <Typography sx={{ color: theme.palette.background.dark, fontSize: 13 }}>Email</Typography>
                                                        </Fragment>
                                                    }
                                                    background={theme.palette.background.default}
                                                    borderRadius={0.5}
                                                    paddingBottom="0px"
                                                    padding="5px 10px"
                                                />
                                                <CommonCard
                                                    content={
                                                        <Fragment>
                                                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{a?.user?.phone_number?.includes('+') ? a?.user?.phone_number : `+${a?.user?.phone_number}`}</Typography>
                                                            <Typography sx={{ color: theme.palette.background.dark, fontSize: 13 }}>Phone Number</Typography>
                                                        </Fragment>
                                                    }
                                                    background={theme.palette.background.default}
                                                    borderRadius={0.5}
                                                    paddingBottom="0px"
                                                    padding="5px 10px"
                                                />
                                                <CommonCard
                                                    content={
                                                        <Fragment>
                                                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{moment(a.created_at).format('DD/MM/YYYY') || '-'}</Typography>
                                                            <Typography sx={{ color: theme.palette.background.dark, fontSize: 13 }}>Invited</Typography>
                                                        </Fragment>
                                                    }
                                                    background={theme.palette.background.default}
                                                    borderRadius={0.5}
                                                    paddingBottom="0px"
                                                    padding="5px 10px"
                                                />
                                                <CommonCard
                                                    content={
                                                        <Fragment>
                                                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{a.accepted_date ? moment(a.accepted_date).format('DD/MM/YYYY') : '-'}</Typography>
                                                            <Typography sx={{ color: theme.palette.background.dark, fontSize: 13 }}>Accepted</Typography>
                                                        </Fragment>
                                                    }
                                                    background={theme.palette.background.default}
                                                    borderRadius={0.5}
                                                    paddingBottom="0px"
                                                    padding="5px 10px"
                                                />
                                            </Box>
                                            <Box
                                                sx={{
                                                    background: theme.palette.cardbackground.primary,
                                                    padding: '10px',
                                                    borderRadius: 1,
                                                    mt: 2,
                                                    border: `1px solid ${theme.palette.cardbackground.light}`
                                                }}
                                            >
                                                <Typography sx={{ fontSize: 12 }} color={theme.palette.cardbackground.middle}>
                                                    {`${a?.user?.first_name} has full read-only access to your dashboard, college wishlist, and calendar.`}
                                                </Typography>
                                                <Typography sx={{ fontSize: 12 }} color={theme.palette.cardbackground.middle}>
                                                    They receive notification when you update your progress.
                                                </Typography>
                                            </Box>
                                        </Fragment>
                                    }
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>
                <Grid item xs={12} md={3} lg={3}>
                    {assigned !== 'DISCOVERY' ? (
                        <Grid container spacing={2} mt={0.2}>
                            <Grid item xs={12} sm={12} md={12}>
                                <StyledCard>
                                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                            <Avatar src={previewUrl} alt="mentor profile" sx={{ width: 56, height: 56 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#1F2937',
                                                        mb: 0.5,
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                    }}
                                                >
                                                    {`${coachData?.mentor?.user?.first_name} ${coachData?.mentor?.user?.last_name}`}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', mb: 1 }}>
                                                    {coachData?.mentor?.degree}
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Star sx={{ fontSize: 16, color: '#FFA500' }} />
                                                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#1F2937' }}>
                                                            {coachData?.mentor?.rating}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                        {coachData?.mentor?.review} Reviews
                                                    </Typography>
                                                    <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                        {coachData?.mentor.experience_years ? `${ExpierienceYears.find((a) => a.value === coachData?.mentor.experience_years)?.label} Exp` : '-'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>

                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                                            {coachData?.mentor.expertise.map((tag, index) => (
                                                <StyledChip key={index} label={tag} size="small" />
                                            ))}
                                        </Box>

                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: '#6B7280',
                                                mb: 2,
                                                lineHeight: 1.5,
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {coachData?.mentor?.bio}
                                        </Typography>

                                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                                            <ViewProfileButton variant="outlined" fullWidth onClick={() => handleViewProfile(coachData)}>
                                                View Profile
                                            </ViewProfileButton>
                                        </Box>
                                    </CardContent>
                                </StyledCard>
                            </Grid>
                        </Grid>
                    ) : (
                        <CommonCard
                            borderRadius={2}
                            paddingBottom="0px"
                            content={
                                <Fragment>
                                    <Typography fontSize={18} fontWeight={600}>Invite a counsellor</Typography>
                                    <Typography color="background.dark" fontSize={13}>
                                        Inviting starts a matching process. She
                                        <br />
                                        will review your profile to ensure a good fit before
                                        <br />
                                        your first session can be scheduled.</Typography>
                                    <CommonCard
                                        borderRadius={1}
                                        border={`1px solid #13A4EC0D`}
                                        background="#13A4EC0D"
                                        content={
                                            <Fragment>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        gap: 0.5,
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <InfoOutlined sx={{ color: '#13A4EC', fontSize: 18 }} />
                                                    <Typography fontSize={14} fontWeight={600}>Matching Policy</Typography>
                                                </Box>
                                                <Typography fontSize={13}>
                                                    You can only have one active counselor. If
                                                    <br />
                                                    accepted, Dr. Smith will replace your current
                                                    <br />
                                                    match in your dashboard.
                                                </Typography>
                                            </Fragment>
                                        }
                                    />
                                    {user?.user_type_id === UserTypeId.Student &&
                                        (<CommonButton
                                            label="Invite A Counselor"
                                            background={theme.palette.secondary.main}
                                            color="white"
                                            marginBottom="0px"
                                            marginTop="10px"
                                            handleClick={() => navigate('/invite-counsellor')}
                                        />)}
                                </Fragment>
                            }
                        />
                    )}
                    <CommonBookSession
                        bookingUrl={process.env.REACT_APP_CALENDLY_SESSION_URL}
                        open={open}
                        setOpen={setOpen}
                        studentName={`${user?.first_name} ${user?.last_name}`}
                        email={user?.email}
                        tutorName={`${coachData?.mentor?.user?.first_name || ''} ${coachData?.mentor?.user?.last_name || ''}`.trim()}
                        mentorProfileId={coachData?.mentor_profile_id}
                        handleClose={(data) => {
                            setOpen(false);
                            setInitialData(data);
                        }}
                    />
                    <CommonCard
                        borderRadius={2}
                        border={`1px solid ${theme.palette.cardbackground.main}`}
                        background={theme.palette.cardbackground.secondary}
                        content={
                            <Fragment>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 1
                                    }}
                                >
                                    <LinkRounded sx={{ color: '#155DFC' }} />
                                    <Typography color="text.dark" fontWeight={600}>Linked parents can view your:</Typography>
                                </Box>
                                <ul
                                    style={{ fontSize: '12px', margin: 0, color: theme.palette.secondary.main }}
                                >
                                    <li>College wishlist and application status</li>
                                    <li>Test scores and academic progress</li>
                                    <li>Counseling Q&A history</li>
                                    <li>Calendar and deadlines</li>
                                </ul>
                                <Typography color="secondary.main" fontSize={12}>They receive notification when you make important updates.</Typography>
                            </Fragment>
                        }
                    />
                </Grid>
                <StyledDialog open={viewProfileDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                    <DialogContent sx={{ p: 4, position: 'relative' }}>
                        <IconButton
                            onClick={handleCloseAll}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: 16,
                                color: '#6B7280',
                            }}
                        >
                            <Close />
                        </IconButton>

                        {selectedMentor && (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                                    <Avatar src={previewUrl} sx={{ width: 120, height: 120, mb: 2 }} />
                                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                                        {`${selectedMentor?.mentor?.user?.first_name} ${selectedMentor?.mentor?.user?.last_name}`}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                                        {selectedMentor?.mentor?.degree}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Star sx={{ fontSize: 20, color: '#FFA500' }} />
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {selectedMentor?.mentor?.rating}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6B7280' }}>
                                            {selectedMentor?.mentor?.review} Reviews
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                            {coachData?.mentor.experience_years ? `${ExpierienceYears.find((a) => a.value === coachData?.mentor.experience_years)?.label} Exp` : '-'}
                                        </Typography>
                                    </Box>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                                        About
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#6B7280', lineHeight: 1.6 }}>
                                        {coachData?.mentor?.bio}
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
                                        Expertise
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {coachData?.mentor?.expertise.map((tag, index) => (
                                            <StyledChip key={index} label={tag} />
                                        ))}
                                    </Box>
                                </Box>

                                {coachData?.mentor?.experiences?.length > 0 && (
                                    <Box sx={{ mb: 4 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                            Experience
                                        </Typography>
                                        {coachData?.mentor?.experiences?.map((exp, index) => (
                                            <Box key={index} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                                <Box
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        borderRadius: '8px',
                                                        backgroundColor: '#F3F4F6',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <img src='/Images/viewProfile.png' alt='ViewProfile' />
                                                </Box>
                                                <Box>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                        {exp.title}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ color: '#6B7280' }}>
                                                        {exp.company} - {exp.period}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Button
                                        variant="outlined"
                                        fullWidth
                                        onClick={handleCloseAll}
                                        sx={{
                                            textTransform: 'none',
                                            borderRadius: '8px',
                                            height: '40px',
                                            borderColor: '#D1D5DB',
                                            color: '#374151',
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    {user?.user_type_id === UserTypeId.Student &&
                                        (<BookSessionButton
                                            fullWidth
                                            onClick={() => {
                                                setOpen(true);
                                                setViewProfileDialog(false);
                                            }}
                                            sx={{ height: '40px' }}
                                        >
                                            Book A Session
                                        </BookSessionButton>)}
                                </Box>
                            </>
                        )}
                    </DialogContent>
                </StyledDialog>
                <StyledDialog open={bookSessionDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                    <DialogContent sx={{ p: 4, position: 'relative' }}>
                        <IconButton
                            onClick={handleCloseAll}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: 16,
                                color: '#6B7280',
                            }}
                        >
                            <Close />
                        </IconButton>

                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                            Confirm Your Session
                        </Typography>

                        {coachData && (
                            <>
                                <Box
                                    sx={{
                                        p: 2,
                                        border: '1px solid #E5E7EB',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        gap: 2,
                                        mb: 3,
                                    }}
                                >
                                    <Avatar src={previewUrl} sx={{ width: 60, height: 60 }} />
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                            {`${coachData?.mentor?.user?.first_name} ${coachData?.mentor?.user?.last_name ? coachData?.mentor?.user?.last_name : ''}`}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#6B7280', mb: 1 }}>
                                            {coachData?.mentor.degree}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Star sx={{ fontSize: 16, color: '#FFA500' }} />
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {coachData?.mentor?.rating}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                {coachData?.mentor?.review} Reviews
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                • {coachData?.mentor.experience_years ? `${ExpierienceYears.find((a) => a.value === coachData?.mentor.experience_years)?.label} Exp` : '-'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>

                                <Grid container spacing={2} sx={{ mb: 3 }}>
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                backgroundColor: '#F9FAFB',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                            }}
                                        >
                                            <img
                                                src='/Images/graycalendar.png'
                                                style={{
                                                    width: 20,
                                                    position: 'relative'
                                                }}
                                                alt=''
                                            />
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {moment(eventData?.session_start_time).format('dddd, MMM YY')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                    Date
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                backgroundColor: '#F9FAFB',
                                                borderRadius: '8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.5,
                                            }}
                                        >
                                            <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {moment(eventData?.session_start_time).format('hh:mm A')}- {moment(eventData?.session_end_time).format('hh:mm A')}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                    Time
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ color: '#374151', mb: 1 }}>
                                        Any specific question for this session
                                    </Typography>
                                    <TextField
                                        fullWidth
                                        multiline
                                        rows={4}
                                        placeholder="Write here"
                                        value={sessionNote}
                                        onChange={(e) => setSessionNote(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                            },
                                        }}
                                    />
                                </Box>

                                <Button
                                    fullWidth
                                    onClick={handleConfirmBooking}
                                    sx={{
                                        backgroundColor: '#1E3A8A',
                                        color: 'white',
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        height: '48px',
                                        fontWeight: 500,
                                        '&:hover': {
                                            backgroundColor: '#1E40AF',
                                        },
                                    }}
                                >
                                    Confirm Booking
                                </Button>
                            </>
                        )}
                    </DialogContent>
                </StyledDialog>
                <StyledDialog open={confirmationDialog} onClose={handleCloseAll} maxWidth="sm" fullWidth>
                    <DialogContent sx={{ p: 4, position: 'relative' }}>
                        <IconButton
                            onClick={handleCloseAll}
                            sx={{
                                position: 'absolute',
                                right: 16,
                                top: 16,
                                color: '#6B7280',
                            }}
                        >
                            <Close />
                        </IconButton>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
                            <img
                                src='/Images/success.png'
                                alt='Check Circle'
                                style={{ width: '70px', height: '70px', marginTop: 20, marginBottom: 10 }}
                            />
                            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                                Booking Confirmed
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#6B7280', textAlign: 'center', mb: 4 }}>
                                Booking Confirmed! Check your email for the meeting link.
                            </Typography>

                            <Grid container spacing={2} sx={{ mb: 4, width: '100%' }}>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            backgroundColor: '#F9FAFB',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                        }}
                                    >
                                        <img
                                            src='/Images/graycalendar.png'
                                            style={{
                                                width: 20,
                                                position: 'relative'
                                            }}
                                            alt=''
                                        />
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                Thursday, Oct 24
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                Date
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                                <Grid item xs={6}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            backgroundColor: '#F9FAFB',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5,
                                        }}
                                    >
                                        <WatchLater sx={{ color: '#6B7280', fontSize: 20 }} />
                                        <Box>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                4:00 PM - 5:00 PM
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#6B7280' }}>
                                                Time
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                                <BookSessionButton fullWidth sx={{ height: '40px' }}>
                                    Go To Dashboard
                                </BookSessionButton>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleCloseAll}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '8px',
                                        height: '40px',
                                        borderColor: '#D1D5DB',
                                        color: '#374151',
                                    }}
                                >
                                    I'll Do It Later
                                </Button>
                            </Box>
                        </Box>
                    </DialogContent>
                </StyledDialog>
            </Grid>
        </Fragment>
    )
}

export default ParentAccount;